import React, { useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { QrCodeIcon, DownloadSimpleIcon, PrinterIcon, CopyIcon, LinkSimpleIcon, ForkKnifeIcon } from "@phosphor-icons/react";

// Standalone QR builder for a property's public digital menu.
// URL scanned: {origin}/menu/{property_id}
export function MenuQR({ activeId, active }) {
  const wrapRef = useRef(null);
  const [tableLabel, setTableLabel] = useState("");
  const [tagline, setTagline] = useState("Scan for our menu");

  const menuUrl = useMemo(() => {
    if (!activeId) return "";
    const base = window.location.origin;
    const qs = tableLabel.trim() ? `?t=${encodeURIComponent(tableLabel.trim())}` : "";
    return `${base}/menu/${activeId}${qs}`;
  }, [activeId, tableLabel]);

  const propertyName = active?.name || "Restaurant";
  const city = active?.city || "Sikkim";

  if (!activeId) {
    return (
      <Card className="p-10 text-center text-muted-foreground text-sm">
        Select a property first to generate its menu QR.
      </Card>
    );
  }

  const svgEl = () => wrapRef.current?.querySelector("svg");

  const downloadSVG = () => {
    const svg = svgEl();
    if (!svg) return;
    const clone = svg.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug(propertyName)}-menu-qr.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("SVG downloaded");
  };

  const downloadPNG = async () => {
    const svg = svgEl();
    if (!svg) return;
    const size = 1024; // high-res
    const src = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const svg64 = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(src)));
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${slug(propertyName)}-menu-qr.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("PNG downloaded");
      }, "image/png");
    };
    img.onerror = () => toast.error("Failed to render PNG");
    img.src = svg64;
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(menuUrl); toast.success("Menu link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const printCard = () => {
    const svg = svgEl();
    if (!svg) return;
    const svgStr = new XMLSerializer().serializeToString(svg);
    const b64 = btoa(unescape(encodeURIComponent(svgStr)));
    const dataUrl = `data:image/svg+xml;base64,${b64}`;
    const w = window.open("", "_blank", "width=520,height=760");
    if (!w) { toast.error("Popup blocked — allow popups to print"); return; }
    w.document.write(`<!doctype html><html><head><title>${escapeHtml(propertyName)} · Menu QR</title>
<meta charset="utf-8" />
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; font-family: 'Inter', system-ui, sans-serif; }
  .card { width: 420px; margin: 32px auto; padding: 28px 28px 22px; border-radius: 20px; text-align: center;
          background: #2F5D50; color: #FFFDF7; border: 2px solid #C8A24B; }
  .brand { font-size: 11px; letter-spacing: .28em; text-transform: uppercase; color: #E7C97A; }
  .title { font-family: 'Playfair Display', 'Times New Roman', serif; font-size: 26px; margin: 10px 0 4px; color: #E7C97A; }
  .sub { font-size: 12px; opacity: .85; margin-bottom: 20px; }
  .qrbox { background: #fff; padding: 14px; border-radius: 14px; display: inline-block; }
  .qrbox img { width: 260px; height: 260px; display: block; }
  .tagline { font-size: 15px; margin-top: 18px; letter-spacing: .03em; }
  .table { margin-top: 8px; font-size: 11px; letter-spacing: .3em; opacity: .8; text-transform: uppercase; }
  .url { margin-top: 14px; font-size: 10px; color: #E7C97A; word-break: break-all; }
  .foot { margin-top: 10px; font-size: 9px; letter-spacing: .18em; text-transform: uppercase; opacity: .7; border-top: 1px solid rgba(231,201,122,.4); padding-top: 10px; }
  @media print { .card { margin: 0 auto; box-shadow: none; } @page { margin: 12mm; } }
</style></head><body>
<div class="card">
  <div class="brand">The King's Group · ${escapeHtml(city)}</div>
  <div class="title">${escapeHtml(propertyName)}</div>
  <div class="sub">Restaurant &amp; Bar</div>
  <div class="qrbox"><img src="${dataUrl}" alt="Menu QR" /></div>
  <div class="tagline">${escapeHtml(tagline || "Scan for our menu")}</div>
  ${tableLabel ? `<div class="table">Table · ${escapeHtml(tableLabel)}</div>` : ""}
  <div class="url">${escapeHtml(menuUrl)}</div>
  <div class="foot">Powered by King's PMS</div>
</div>
<script>window.addEventListener('load', () => { setTimeout(() => { window.focus(); window.print(); }, 300); });</script>
</body></html>`);
    w.document.close();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="menu-qr-panel">
      <Card className="p-6 space-y-4">
        <div>
          <p className="label-overline flex items-center gap-1 text-muted-foreground"><QrCodeIcon size={14} weight="duotone" /> Guest menu QR</p>
          <h3 className="font-display text-2xl mt-1">{propertyName}</h3>
          <p className="text-sm text-muted-foreground">Guests scan this to browse your live menu on their phone — no app, no login.</p>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Tagline (printed under the QR)</Label>
            <Input value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="Scan for our menu" data-testid="menu-qr-tagline" />
          </div>
          <div>
            <Label className="text-xs">Optional table label</Label>
            <Input value={tableLabel} onChange={(e) => setTableLabel(e.target.value)} placeholder="e.g. T-05" data-testid="menu-qr-table" />
            <p className="text-[10px] text-muted-foreground mt-1">Appended to the URL as <code>?t=</code> so staff can trace which table the scan came from.</p>
          </div>
        </div>

        <div className="rounded-md border border-border bg-muted/30 p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><LinkSimpleIcon size={12} /> Menu URL</p>
          <p className="font-mono-plex text-xs mt-1 break-all" data-testid="menu-qr-url">{menuUrl}</p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="menu-qr-copy"><CopyIcon size={14} className="mr-1" />Copy link</Button>
            <a className="inline-flex" href={menuUrl} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" data-testid="menu-qr-open"><ForkKnifeIcon size={14} className="mr-1" />Preview menu</Button>
            </a>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={printCard} data-testid="menu-qr-print"><PrinterIcon size={14} className="mr-1" />Print</Button>
          <Button variant="outline" onClick={downloadPNG} data-testid="menu-qr-download-png"><DownloadSimpleIcon size={14} className="mr-1" />PNG</Button>
          <Button variant="outline" onClick={downloadSVG} data-testid="menu-qr-download-svg"><DownloadSimpleIcon size={14} className="mr-1" />SVG</Button>
        </div>
      </Card>

      {/* Live preview card — resembles what will print */}
      <Card className="p-0 overflow-hidden">
        <div ref={wrapRef} className="bg-primary text-primary-foreground p-8 flex flex-col items-center text-center" data-testid="menu-qr-preview">
          <p className="text-[10px] uppercase tracking-[0.28em] text-gold">The King&apos;s Group · {city}</p>
          <h4 className="font-display text-2xl text-gold mt-2">{propertyName}</h4>
          <p className="text-xs opacity-80 mt-0.5">Restaurant &amp; Bar</p>
          <div className="bg-white rounded-xl p-3 mt-6">
            <QRCodeSVG
              value={menuUrl}
              size={220}
              level="M"
              includeMargin={false}
              fgColor="#2F5D50"
              bgColor="#FFFFFF"
            />
          </div>
          <p className="mt-5 text-sm tracking-wide">{tagline || "Scan for our menu"}</p>
          {tableLabel && <p className="mt-1 text-[10px] uppercase tracking-[0.3em] opacity-80">Table · {tableLabel}</p>}
          <p className="mt-4 text-[10px] text-gold/90 break-all max-w-[260px]">{menuUrl}</p>
          <p className="mt-3 text-[9px] uppercase tracking-[0.18em] opacity-70 border-t border-gold/30 pt-2 w-full">Powered by King&apos;s PMS</p>
        </div>
      </Card>
    </div>
  );
}

function slug(s) {
  return String(s || "menu").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "menu";
}
function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
